import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSidebar } from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import WorkspaceHeader from './components/WorkspaceHeader';
import TeamAssignmentPanel from './components/TeamAssignmentPanel';
import IncidentTimeline from './components/IncidentTimeline';
import RootCauseAnalysis from './components/RootCauseAnalysis';
import EvidencePanel from './components/EvidencePanel';
import RaiseRecommendationPanel from './components/RaiseRecommendationPanel';
import SiteApproverReview from './components/SiteApproverReview';
import RecommendationClosurePanel from './components/RecommendationClosurePanel';
import CrossSiteApplicabilityPanel from './components/CrossSiteApplicabilityPanel';
import AuditTrailPanel from './components/AuditTrailPanel';
import { releasedIncidents } from '../incident-creation-form/index';

const staticMockInvestigations = [
  {
    id: 1, incidentId: 'INC-2025-001',
    title: 'Chemical spill investigation - Production Area 2',
    incidentType: 'injury', severity: 'critical', status: 'findings-documented',
    investigator: 'Dr. Sarah Johnson', location: 'plant-1',
    incidentDate: '2025-01-10T14:30:00', targetCompletionDate: '2025-01-20T17:00:00',
    progress: 65, recommendations: 3, implementedRecommendations: 1,
    documentsCollected: 12, interviewsCompleted: 8, rootCauseIdentified: true,
    approvalHistory: [{ action: 'rejected', by: 'Site Approver', comment: 'Please add more detail on contributing factors', at: '2025-01-11T10:00:00' }],
  },
  {
    id: 2, incidentId: 'INC-2025-002',
    title: 'Near miss - Forklift collision averted',
    incidentType: 'near-miss', severity: 'moderate', status: 'approved',
    investigator: 'Michael Chen', location: 'warehouse-1',
    incidentDate: '2024-12-28T10:15:00', targetCompletionDate: '2025-01-05T17:00:00',
    progress: 100, recommendations: 5, implementedRecommendations: 5,
    documentsCollected: 8, interviewsCompleted: 5, rootCauseIdentified: true,
    approvalHistory: [{ action: 'approved', by: 'Site Approver', comment: 'Thorough investigation, well documented.', at: '2025-01-03T14:00:00' }],
  },
  {
    id: 3, incidentId: 'INC-2025-003',
    title: 'Minor injury - Slip and fall in cafeteria',
    incidentType: 'injury', severity: 'minor', status: 'in-progress',
    investigator: 'Emily Rodriguez', location: 'plant-2',
    incidentDate: '2025-01-12T12:45:00', targetCompletionDate: '2025-01-18T17:00:00',
    progress: 40, recommendations: 2, implementedRecommendations: 0,
    documentsCollected: 5, interviewsCompleted: 3, rootCauseIdentified: false,
    approvalHistory: [],
  },
  {
    id: 4, incidentId: 'INC-2025-004',
    title: 'Equipment failure investigation - Hydraulic press',
    incidentType: 'near-miss', severity: 'major', status: 'overdue',
    investigator: 'Robert Thompson', location: 'plant-1',
    incidentDate: '2024-12-20T08:00:00', targetCompletionDate: '2025-01-10T17:00:00',
    progress: 75, recommendations: 4, implementedRecommendations: 1,
    documentsCollected: 15, interviewsCompleted: 10, rootCauseIdentified: true,
    approvalHistory: [],
  },
  {
    id: 5, incidentId: 'INC-2025-005',
    title: 'Fire alarm system malfunction investigation',
    incidentType: 'near-miss', severity: 'critical', status: 'initiated',
    investigator: 'Lisa Anderson', location: 'plant-3',
    incidentDate: '2025-01-15T16:20:00', targetCompletionDate: '2025-01-25T17:00:00',
    progress: 10, recommendations: 0, implementedRecommendations: 0,
    documentsCollected: 2, interviewsCompleted: 1, rootCauseIdentified: false,
    approvalHistory: [],
  },
];

const defaultRcaData = {
  rcaMethod: '5-whys',
  findings: '',
  rootCauseSummary: '',
  contributingFactors: [],
  fiveWhys: [],
};

const defaultTeamData = { leadId: 'u1', memberIds: ['u3', 'u4'] };

const defaultTimeline = [
  { id: 1, time: new Date()?.toISOString(), description: 'Investigation initiated', actor: 'System', type: 'action' },
];

const defaultRecommendations = [
  {
    id: 101,
    type: 'corrective',
    description: 'Conduct immediate inspection of all chemical storage containers and replace any damaged or non-compliant units.',
    ownerId: 'u3',
    dueDate: '2025-02-10',
    priority: 'critical',
    effectivenessCheckDue: '2025-03-10',
    status: 'raised',
    raisedAt: '2025-01-10T16:00:00',
  },
  {
    id: 102,
    type: 'preventive',
    description: 'Implement mandatory chemical handling refresher training for all production area personnel.',
    ownerId: 'u1',
    dueDate: '2025-02-28',
    priority: 'high',
    effectivenessCheckDue: '2025-04-01',
    status: 'in-progress',
    raisedAt: '2025-01-11T09:00:00',
  },
  {
    id: 103,
    type: 'preventive',
    description: 'Install secondary containment bunds around all chemical storage areas in Production Area 2.',
    ownerId: 'u5',
    dueDate: '2025-03-15',
    priority: 'medium',
    effectivenessCheckDue: '',
    status: 'raised',
    raisedAt: '2025-01-11T10:30:00',
  },
];

const PHASE_A_TABS = [
  { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
  { id: 'timeline', label: 'Timeline', icon: 'Clock' },
  { id: 'rca', label: 'Root Cause Analysis', icon: 'Search' },
  { id: 'evidence', label: 'Evidence', icon: 'Paperclip' },
  { id: 'recommendations', label: 'Recommendations', icon: 'CheckSquare' },
  { id: 'approval', label: 'Approver Review', icon: 'ShieldCheck' },
];

const PHASE_B_TABS = [
  { id: 'closure', label: 'Recommendation Closure', icon: 'CheckCircle2' },
  { id: 'cross-site', label: 'Cross-Site Applicability', icon: 'Globe' },
  { id: 'audit', label: 'Audit Trail', icon: 'Activity' },
];

const OverviewCard = ({ label, value, icon, color }) => (
  <div className="bg-muted/40 rounded-xl p-4 flex items-center gap-3">
    <div className={`p-2.5 rounded-lg ${color}`}>
      <Icon name={icon} size={18} className="text-white" />
    </div>
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold text-foreground">{value}</p>
    </div>
  </div>
);

const IncidentInvestigationWorkspace = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Resolve investigation ID from route params or navigation state
  const rawId = params?.id || location?.state?.investigationId;
  const investigationId = rawId ? (isNaN(Number(rawId)) ? rawId : Number(rawId)) : 1;

  // Merge static mock investigations with any released incidents
  const allInvestigations = [...staticMockInvestigations, ...releasedIncidents];

  // Find by numeric id or string incidentId
  const foundInvestigation = allInvestigations?.find(i =>
    i?.id === investigationId || i?.id === String(investigationId) || i?.incidentId === String(investigationId)
  );

  // For newly released incidents, build a workspace-compatible object
  const buildInvestigationFromReleased = (released) => ({
    ...released,
    status: released?.status || 'initiated',
    approvalHistory: released?.approvalHistory || [],
    documentsCollected: released?.documentsCollected || 0,
    interviewsCompleted: released?.interviewsCompleted || 0,
    rootCauseIdentified: released?.rootCauseIdentified || false,
    progress: released?.progress || 0,
  });

  const investigation = foundInvestigation
    ? buildInvestigationFromReleased(foundInvestigation)
    : buildInvestigationFromReleased(staticMockInvestigations?.[0]);

  // For newly released incidents, start with empty recommendations and a fresh timeline
  const isNewlyReleased = releasedIncidents?.some(r => r?.id === investigationId);

  const initialTimeline = isNewlyReleased
    ? [{ id: 1, time: investigation?.incidentDate || new Date()?.toISOString(), description: 'Incident reported and released for investigation', actor: 'Reporter', type: 'incident' }]
    : defaultTimeline;

  const initialRecommendations = isNewlyReleased ? [] : defaultRecommendations;

  const [currentRole, setCurrentRole] = useState('lead');
  const [activePhase, setActivePhase] = useState('A');
  const [activeTab, setActiveTab] = useState('overview');
  const [investigationData, setInvestigationData] = useState(investigation);
  const [teamData, setTeamData] = useState(defaultTeamData);
  const [rcaData, setRcaData] = useState(defaultRcaData);
  const [timeline, setTimeline] = useState(initialTimeline);
  const [attachments, setAttachments] = useState([]);
  const [recommendations, setRecommendations] = useState(initialRecommendations);
  const [siteTracking, setSiteTracking] = useState([]);

  const isApproved = ['approved', 'closed', 'completed']?.includes(investigationData?.status);

  const handleSubmitForApproval = () => {
    setInvestigationData(prev => ({ ...prev, status: 'findings-documented' }));
  };

  const handleApprove = ({ comment }) => {
    setInvestigationData(prev => ({
      ...prev,
      status: 'approved',
      approvalHistory: [...(prev?.approvalHistory || []), { action: 'approved', by: 'Site Approver', comment, at: new Date()?.toISOString() }],
    }));
  };

  const handleReject = ({ reason }) => {
    setInvestigationData(prev => ({
      ...prev,
      status: 'in-progress',
      approvalHistory: [...(prev?.approvalHistory || []), { action: 'rejected', by: 'Site Approver', comment: reason, at: new Date()?.toISOString() }],
    }));
  };

  const handleAddRecommendation = (rec) => {
    setRecommendations(prev => [...prev, rec]);
  };

  const handleUpdateRecommendation = (updated) => {
    setRecommendations(prev => prev?.map(r => r?.id === updated?.id ? { ...r, ...updated } : r));
  };

  const handleUpdateTracking = (tracking) => {
    setSiteTracking(prev => {
      const existing = prev?.findIndex(t => t?.id === tracking?.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated?.[existing], ...tracking };
        return updated;
      }
      return [...prev, tracking];
    });
  };

  const tabs = activePhase === 'A' ? PHASE_A_TABS : PHASE_B_TABS;

  const renderTabContent = () => {
    if (activePhase === 'A') {
      switch (activeTab) {
        case 'overview':
          return (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <OverviewCard label="Progress" value={`${investigationData?.progress}%`} icon="TrendingUp" color="bg-primary" />
                <OverviewCard label="Recommendations" value={recommendations?.length} icon="CheckSquare" color="bg-emerald-600" />
                <OverviewCard label="Evidence Files" value={attachments?.length + (investigationData?.documentsCollected || 0)} icon="Paperclip" color="bg-blue-600" />
                <OverviewCard label="Interviews" value={investigationData?.interviewsCompleted || 0} icon="Users" color="bg-purple-600" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TeamAssignmentPanel teamData={teamData} onTeamChange={setTeamData} readOnly={currentRole !== 'approver'} />
                <SiteApproverReview
                  investigation={investigationData}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  currentRole={currentRole}
                />
              </div>
            </div>
          );
        case 'timeline':
          return (
            <IncidentTimeline
              events={timeline}
              onAddEvent={e => setTimeline(prev => [...prev, e])}
              onRemoveEvent={id => setTimeline(prev => prev?.filter(e => e?.id !== id))}
            />
          );
        case 'rca':
          return <RootCauseAnalysis rcaData={rcaData} onRcaChange={setRcaData} />;
        case 'evidence':
          return (
            <EvidencePanel
              attachments={attachments}
              onAddAttachment={a => setAttachments(prev => [...prev, a])}
              onRemoveAttachment={id => setAttachments(prev => prev?.filter(a => a?.id !== id))}
            />
          );
        case 'recommendations':
          return (
            <RaiseRecommendationPanel
              recommendations={recommendations}
              onAddRecommendation={handleAddRecommendation}
              onUpdateRecommendation={handleUpdateRecommendation}
              investigationApproved={true}
            />
          );
        case 'approval':
          return (
            <SiteApproverReview
              investigation={investigationData}
              onApprove={handleApprove}
              onReject={handleReject}
              currentRole={currentRole}
            />
          );
        default:
          return null;
      }
    } else {
      switch (activeTab) {
        case 'closure':
          return (
            <RecommendationClosurePanel
              recommendations={recommendations}
              onUpdateRecommendation={handleUpdateRecommendation}
            />
          );
        case 'cross-site':
          return (
            <CrossSiteApplicabilityPanel
              recommendations={recommendations}
              siteTracking={siteTracking}
              onUpdateTracking={handleUpdateTracking}
            />
          );
        case 'audit':
          return <AuditTrailPanel auditLog={investigationData?.auditLog} />;
        default:
          return null;
      }
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <div className="p-4 lg:p-6 max-w-7xl mx-auto">
        {/* Back navigation */}
        <button
          onClick={() => navigate('/incident-investigation-tracker')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <Icon name="ArrowLeft" size={16} />
          Back to Investigation Tracker
        </button>

        <WorkspaceHeader
          investigation={investigationData}
          onSubmitForApproval={handleSubmitForApproval}
          onApprove={handleApprove}
          onReject={handleReject}
          currentRole={currentRole}
        />

        {/* Role switcher (demo only) */}
        <div className="mb-4 flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <Icon name="Info" size={15} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-xs text-amber-700 dark:text-amber-300 flex-1">Demo: Switch role to test different views</span>
          <div className="flex gap-1">
            {['lead', 'approver']?.map(role => (
              <button
                key={role}
                onClick={() => setCurrentRole(role)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors capitalize ${
                  currentRole === role ? 'bg-amber-600 text-white' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 hover:bg-amber-200'
                }`}
              >
                {role === 'lead' ? '🔬 Investigation Lead' : '✅ Site Approver'}
              </button>
            ))}
          </div>
        </div>

        {/* Phase Switcher */}
        <div className="mb-5 flex items-center gap-3 flex-wrap">
          <div className="bg-card border border-border rounded-xl p-1 flex gap-1">
            <button
              onClick={() => { setActivePhase('A'); setActiveTab('overview'); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activePhase === 'A' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="ClipboardList" size={15} />
              Phase A — Investigation
            </button>
            <button
              onClick={() => { setActivePhase('B'); setActiveTab('closure'); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activePhase === 'B' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="GitBranch" size={15} />
              Phase B — Recommendation Tracking
            </button>
          </div>
          {!isApproved && activePhase === 'B' && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <Icon name="AlertCircle" size={13} />
              <span>Investigation not yet approved</span>
            </div>
          )}
          {activePhase === 'A' && (
            <div className="ml-auto">
              {isApproved ? (
                <div className="flex items-center gap-1.5 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm font-medium rounded-xl">
                  <Icon name="CheckCircle2" size={15} />
                  Investigation Approved
                </div>
              ) : investigationData?.status === 'findings-documented' ? (
                <div className="flex items-center gap-1.5 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-sm font-medium rounded-xl">
                  <Icon name="Clock" size={15} />
                  Pending Approver Review
                </div>
              ) : (
                <button
                  onClick={handleSubmitForApproval}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors shadow-sm"
                >
                  <Icon name="Send" size={15} />
                  Submit for Approval
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="mb-5 overflow-x-auto">
          <div className="flex gap-1 bg-card border border-border rounded-xl p-1 w-fit min-w-full sm:min-w-0">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={13} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div>{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default IncidentInvestigationWorkspace;
